import { memo, useCallback, useState, MouseEvent } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Chip,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import ClearIcon from '@mui/icons-material/Clear';
import { bloxValues } from 'themes/constants';
import { buildViewAtSiteUrl } from 'utility/functions';
import { useTranslation } from 'react-i18next';
import { normalizeTestIdValue } from 'utility/functions';

import { getMiniMenuIconSx, miniMenuSx } from 'components/Minimenus/miniMenuStyles';

export type SiteTagItemMenuProps = {
  domain: string | null | undefined;
  originDomain: string | undefined;
  onSetOriginDomain: ((domain: string) => void) | undefined;
  assetUuid?: string;
  disabled?: boolean;
  disableOriginActions?: boolean;
  siteTagName?: string;
};

export const SiteTagItemMenu = memo(
  ({
    domain,
    originDomain,
    onSetOriginDomain,
    assetUuid,
    disabled,
    disableOriginActions,
    siteTagName,
  }: SiteTagItemMenuProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);
    const isAlreadyOrigin = Boolean(domain && domain === originDomain);
    const { t } = useTranslation();
    const iconSx = getMiniMenuIconSx(theme);

    const handleOpen = useCallback((e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    }, []);

    const handleMouseDown = useCallback((e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleToggleOrigin = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (onSetOriginDomain) {
          if (isAlreadyOrigin) {
            onSetOriginDomain('');
          } else if (domain) {
            onSetOriginDomain(domain);
          }
        }
        handleClose();
      },
      [domain, isAlreadyOrigin, onSetOriginDomain, handleClose],
    );

    const handleViewAtSite = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (domain && assetUuid) {
          const url = buildViewAtSiteUrl(domain, assetUuid);
          window.open(url, '_blank');
        }
        handleClose();
      },
      [domain, assetUuid, handleClose],
    );

    return (
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {isAlreadyOrigin && (
          <Chip
            data-testid="site-tag-origin-badge"
            icon={<VerifiedIcon />}
            label={t('blox.general.origin')}
            size="small"
            color="primary"
            sx={{
              mx: 0.5,
              height: 'auto',
              fontSize: bloxValues.tinyFontSizeRem,
              '& .MuiChip-icon': {
                fontSize: bloxValues.tinyFontSizeRem,
              },
              '& .MuiChip-label': { py: 0.25 },
            }}
          />
        )}
        <IconButton
          data-testid={`site-tag-menu-toggle-${normalizeTestIdValue(siteTagName)}`}
          aria-label="site tag options"
          size="small"
          onMouseDown={handleMouseDown}
          onClick={handleOpen}
          sx={{
            flexShrink: 0,
            color: theme.palette.action.disabled,
            '& svg': { fontSize: bloxValues.iconSizeRegular },
            '&:hover': { color: theme.palette.text.secondary },
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleClose}
          sx={{
            ...miniMenuSx,
            '& .MuiPaper-root': {
              borderRadius: 1,
              minWidth: 150,
            },
            '& .MuiList-root': { padding: 0 },
          }}
        >
          <MenuItem
            data-testid={`site-tag-menu-view-at-site-${normalizeTestIdValue(siteTagName)}`}
            disabled={!domain || !assetUuid || Boolean(disabled)}
            onClick={handleViewAtSite}
          >
            <OpenInNewIcon
              color="info"
              fontSize="small"
              sx={iconSx}
            />
            <ListItemText primary={t('blox.general.view_at_site')} />
          </MenuItem>
          <MenuItem
            data-testid={`site-tag-menu-toggle-origin-${normalizeTestIdValue(siteTagName)}`}
            disabled={
              Boolean(disabled) ||
              Boolean(disableOriginActions) ||
              !onSetOriginDomain ||
              (!isAlreadyOrigin && !domain)
            }
            onClick={handleToggleOrigin}
          >
            {isAlreadyOrigin ? (
              <ClearIcon
                color="info"
                fontSize="small"
                sx={iconSx}
              />
            ) : (
              <VerifiedIcon
                color="info"
                fontSize="small"
                sx={iconSx}
              />
            )}
            <ListItemText
              primary={
                isAlreadyOrigin
                  ? t('blox.general.remove_origin', 'Remove Origin')
                  : t('blox.general.set_as_origin')
              }
            />
          </MenuItem>
        </Menu>
      </Box>
    );
  },
);

SiteTagItemMenu.displayName = 'SiteTagItemMenu';
